import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FileUploadControl,
  FileUploadValidators,
} from '@iplab/ngx-file-upload';
import {
  AssetsDocs,
  CategoryDocs,
  EmployeeDocs,
} from 'src/app/constants/documentType';
import { FileUploadService } from 'src/app/services/file-upload.service';
import { MessageService } from '../message/message.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { User } from 'src/app/model/User';
import { saveAs as importedSaveAs } from 'file-saver';
import { LoaderService } from 'src/app/services/loader.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.css'],
})
export class UploadComponent implements OnInit, OnChanges, OnDestroy {
  displayedColumns: string[] = [
    'documentId',
    'category',
    'fileName',
    'documentDesc',
    'documentPath',
    'status',
    'updatedBy',
    'updateDate',
    'action',
  ];

  filesList: FilesList[] = [];
  dataSource = new MatTableDataSource<FilesList>(this.filesList);

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('fileDropRef', { static: false }) fileDropEl: ElementRef;

  @Input() fileList: string;
  @Input() category: string;

  loading$ = this.loader.loading$;
  public fileUploadControl = new FileUploadControl({ listVisible: false });

  selectedOption: string;
  documentsList: any[] = EmployeeDocs;
  reqType: string;
  user: User;
  categoryType: string;
  categoryDesc: string;
  selectedFiles: File[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private uploadService: FileUploadService,
    private messageService: MessageService,
    private loader: LoaderService,
  ) {}

  ngOnInit(): void {
    this.user = JSON.parse(localStorage.getItem('userDetails') || '{}') as User;
    this.dataSource.paginator = this.paginator;
    this.initializeDocumentConfig(this.fileList);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['category'] && changes['category'].currentValue) {
      this.categoryType = changes['category'].currentValue;
      this.initializeDocumentConfig(this.fileList);
      this.fetchDocuments(this.categoryType);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeDocumentConfig(config: string): void {
    if (config === 'employee') {
      this.documentsList = EmployeeDocs;
      this.reqType = 'E';
      this.categoryDesc = 'Employee';
    } else if (config === 'assets') {
      this.documentsList = AssetsDocs;
      this.reqType = 'A';
      this.categoryDesc = 'Asset';
    } else {
      this.documentsList = CategoryDocs;
      this.categoryDesc = config;
      this.reqType = config;
    }
  }

  fetchDocuments(category: string): void {
    this.filesList = [];
    this.dataSource = new MatTableDataSource<FilesList>(this.filesList);

    this.uploadService
      .fetchAllDocuments(category.replace('/', '=='))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.filesList = res.map(
            (e) =>
              new FilesList(
                e.documentId,
                e.category,
                e.filename,
                e.documentDescription,
                e.documentPath,
                e.documentStatus,
                e.uploadedBy,
                e.empAssignedDate,
              ),
          );
          this.dataSource = new MatTableDataSource<FilesList>(this.filesList);
          this.dataSource.paginator = this.paginator;
        },
        error: (err) => {
          console.error('Error fetching documents:', err);
          this.messageService.openSnackBar('Failed to fetch files', 'Error');
        },
      });
  }

  upload(): void {
    if (!this.selectedOption) {
      this.messageService.openSnackBar('Please select a file type', 'Warning');
      return;
    }

    this.selectedFiles = this.fileUploadControl.value || [];

    if (!this.selectedFiles.length) {
      this.messageService.openSnackBar('Please select files', 'Warning');
      return;
    }

    if (this.selectedFiles.length > 1) {
      this.uploadMultipleFiles(this.selectedFiles);
    } else {
      this.uploadSingleFile(this.selectedFiles[0]);
    }
  }

  private uploadMultipleFiles(files: File[]): void {
    const formData = new FormData();
    formData.append('fileType', this.selectedOption);
    formData.append('category', this.categoryType);
    formData.append('user', this.user.empId);
    formData.append('reqType', this.reqType);

    files.forEach((file) => {
      formData.append('filesList', file);
    });

    this.uploadService
      .uploadMultiple(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.openSnackBar(
            'Files uploaded successfully',
            'Success',
          );
          this.resetUploadState();
          this.fetchDocuments(this.categoryType);
        },
        error: (err) => {
          console.error('Error uploading documents:', err);
          this.messageService.openSnackBar('Failed to upload files', 'Error');
        },
      });
  }

  private uploadSingleFile(file: File): void {
    this.uploadService
      .upload(
        file,
        this.selectedOption,
        this.categoryType,
        this.user.empId,
        this.reqType,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.openSnackBar(
            'File uploaded successfully',
            'Success',
          );
          this.resetUploadState();
          this.fetchDocuments(this.categoryType);
        },
        error: (err) => {
          console.error('Error uploading document:', err);
          this.messageService.openSnackBar('Failed to upload file', 'Error');
        },
      });
  }

  private resetUploadState(): void {
    this.selectedFiles = [];
    this.fileUploadControl = new FileUploadControl({ listVisible: false });
  }

  refresh(): void {
    this.fetchDocuments(this.categoryType);
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  onDelete(id: string, path: string): void {
    this.uploadService
      .deleteDocument(id, path.replace(/\//g, '@'))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.openSnackBar(
            'File deleted successfully',
            'Success',
          );
          this.fetchDocuments(this.categoryType);
        },
        error: (err) => {
          console.error('Error deleting document:', err);
          this.messageService.openSnackBar(
            'Failed to delete document',
            'Error',
          );
        },
      });
  }

  onDownload(id: string, path: string): void {
    this.uploadService
      .downloadDocuments(path.replace(/\//g, '@'))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          window.open(res.url);
          const blob = new Blob([res], { type: 'text/json; charset=utf-8' });
          importedSaveAs(blob, id);
        },
        error: (err) => {
          console.error('Error downloading document:', err);
          this.messageService.openSnackBar('Failed to download file', 'Error');
        },
      });
  }
}

export class FilesList {
  constructor(
    readonly documentId: number,
    readonly category: string,
    readonly fileName: string,
    readonly documentDesc: string,
    readonly documentPath: string,
    readonly status: string,
    readonly updatedBy: string,
    readonly updateDate: Date,
  ) {}
}
